import 'components/DiscoveryTasks/DiscoveryTasks.css';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { type FC, memo, useState } from 'react';
import { type UseFormReturn, useFieldArray, useWatch } from 'react-hook-form';
import { useApi } from 'utils/request/useApi';
import { useDiscoverySettingsContext } from '../../composition/DiscoverySettings/discoverySettingsContext';
import { ManualLinkForm } from './ManualLinkForm';
import { ManualLinksList } from './ManualLinksList';
import type { PublicManualLink } from './model/ManualLinksModel';

export type SettingsType = 'snapshot' | 'global';

interface Props {
  form: UseFormReturn<{ manualLinks: PublicManualLink[] }>;
}

const ManualLinksSettings: FC<Props> = ({ form }) => {
  const { showUpdateFromGlobalSettingsButton } = useDiscoverySettingsContext();
  const api = useApi();
  const confirmDialog = useSimpleConfirmDialog({
    btnConfirmAccent: 'negative',
    btnCancelAccent: 'positive',
  });
  const [editedItem, setEditedItem] = useState<PublicManualLink | 'new' | null>(
    null,
  );
  const [updatingFromGlobal, setUpdatingFromGlobal] = useLoadingState();
  const isSnapshotSpecific = showUpdateFromGlobalSettingsButton ?? false;
  const manualLinks = useWatch({ control: form.control, name: 'manualLinks' });

  const { append, replace, remove, update } = useFieldArray({
    control: form.control,
    name: 'manualLinks',
  });

  const handleDeleteMany = (ids: Set<string>) => {
    const indicesToRemove = manualLinks.reduce<number[]>((acc, link, index) => {
      if (ids.has(link.id)) {
        acc.push(index);
      }
      return acc;
    }, []);

    if (indicesToRemove.length > 0) {
      remove(indicesToRemove);
    }
  };

  const handleUseGlobalSettings = async () => {
    if (
      await confirmDialog.show({
        btnConfirm: 'Overwrite',
        btnSecondaryConfirm: 'Cancel',
        header: 'Update Manual Device Links from global settings',
        subHeader: 'Overwrite current settings?',
      })
    ) {
      await withLoadingIndicator(setUpdatingFromGlobal)(async () => {
        const settings = await api.request<any>({
          url: '/settings',
          method: 'GET',
        });
        replace(settings.manualLinks || []);
      });
    }
  };

  return (
    <>
      {confirmDialog.render()}
      {editedItem !== null && (
        <ManualLinkForm
          form={form}
          isSnapshotSpecific={isSnapshotSpecific}
          editedItem={editedItem}
          onAddItem={(item) => append(item)}
          onUpdateItem={(index, item) => update(index, item)}
          onClose={() => setEditedItem(null)}
        />
      )}
      <ManualLinksList
        isSnapshotSpecific={isSnapshotSpecific}
        loading={updatingFromGlobal === 'LOADING'}
        manualLinks={manualLinks}
        onDeleteMany={handleDeleteMany}
        onUseGlobalSettings={handleUseGlobalSettings}
        setEditedItem={setEditedItem}
      />
    </>
  );
};

export const ManualDeviceLinks = memo(ManualLinksSettings);
